/**
 * Per-user onboarding completion in localStorage.
 *
 * Key format: `psp_onboarding_completed:<auth0UserSub>`
 * Must stay in sync with platform-onboarding-module (remote writes the same key
 * using `options.shell.auth0.getAuth0State().user?.sub`).
 */

const ONBOARDING_STORAGE_KEY_PREFIX = 'psp_onboarding_completed:';

/**
 * E2E / test kill switch. When `sessionStorage['psp.suppressOnboarding'] === 'true'`,
 * PSP skips the first-run `/onboarding` redirect even if the LaunchDarkly flag is on.
 * sessionStorage clears when the tab closes — safe for automated browsers.
 */
export const SUPPRESS_ONBOARDING_SESSION_KEY = 'psp.suppressOnboarding';

/** Builds the localStorage key for a given Auth0 user `sub`. */
export function onboardingStorageKey(userId: string): string {
  return `${ONBOARDING_STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * True when automated tests (or a manual sessionStorage override) have disabled
 * the onboarding redirect. Takes precedence over the LaunchDarkly flag.
 */
export function isOnboardingSuppressed(): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(SUPPRESS_ONBOARDING_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * True when this Auth0 user has completed or dismissed onboarding.
 * Returns false when `userId` is missing (caller should wait for auth).
 */
export function hasCompletedOnboarding(userId: string | undefined | null): boolean {
  if (!userId) {
    return false;
  }
  return window.localStorage?.getItem(onboardingStorageKey(userId)) === 'true';
}

/**
 * Marks onboarding complete for this Auth0 user.
 * Used by the shell escape hatch when the remote module fails to load, and by
 * tests. The remote module should write the same key on successful completion,
 * then navigate away from `/onboarding`.
 */
export function markOnboardingComplete(userId: string): void {
  window.localStorage?.setItem(onboardingStorageKey(userId), 'true');
}

/** @internal Exposed for tests only. */
export const ONBOARDING_STORAGE_KEY_PREFIX_FOR_TESTS = ONBOARDING_STORAGE_KEY_PREFIX;
